import { h, Fragment } from "preact";
import { useState, useEffect, useMemo, useRef } from "preact/hooks";
import useProduct from "../../helpers/useProduct/useProduct";
import unifyGroupOptions from "../../helpers/unifyGroupOptions";
import unifyGroupVariants from "../../helpers/unifyGroupVariants";
import { IdPrefixContext } from "../../helpers/idPrefixContext";
import ProductOption from "./product-option";
import variantHasOptionValue from "../../helpers/variantHasOptionValue";
import ProductOptionVariant from "./product-option-variant";
import SizeGuideTrigger from "../size-guide/size-guide-trigger";
import hasStyleCategorySwatches from "../../helpers/hasStyleCategorySwatches";
import { resolveInitialOptionValues } from "../../helpers/resolveInitialVariant";

const optionValueExists = ({ options, optionName, optionValue }) =>
  options.some(
    (option) =>
      option.optionName === optionName &&
      option.optionValues.some(({ name }) => optionValue === name),
  );

const OPTION_ROLES = {
  primary: "primary",
};

export default function ProductOptionsMulti({
  productId,
  idPrefix,
  initialVariantId,
  selectedVariants,
  setSelectedVariants,
  setPreviewVariant,
  addedToCart,
  allowMaterialGroups = true,
  truncate = false,
  smallMonogram = false,
  swatchRequestEnabled,
}) {
  const [productData] = useProduct(productId);
  const ref = useRef(null);
  const [options, setOptions] = useState([]);
  const [variants, setVariants] = useState([]);
  const [selectedOptionValues, setSelectedOptionValues] = useState({});
  const [selectedQuantities, setSelectedQuantities] = useState({});
  // Per-line monograms, keyed by the variant's last option value (like
  // selectedQuantities).
  const [selectedMonograms, setSelectedMonograms] = useState({});

  const swatchRequestUrl =
    swatchRequestEnabled === "true" && hasStyleCategorySwatches(productData)
      ? window.productUrl(productData.handle)
      : null;

  const variantOptionValue = (variant) =>
    variant.selectedOptions[variant.selectedOptions.length - 1].value;

  const selectedQuantityForVariant = (variant) => {
    const value = variantOptionValue(variant);

    return value in selectedQuantities ? selectedQuantities[value] : 0;
  };

  const setSelectedQuantityForVariant = (variant, quantity) => {
    const value = variantOptionValue(variant);
    const newQuantities = {
      ...selectedQuantities,
      [value]: quantity,
    };
    setSelectedQuantities(newQuantities);
  };

  const monogramForVariant = (variant) =>
    selectedMonograms[variantOptionValue(variant)] || null;

  const setMonogramForVariant = (variant, monogram) => {
    setSelectedMonograms((current) => ({
      ...current,
      [variantOptionValue(variant)]: monogram,
    }));
  };

  const matchingVariants = useMemo(() => {
    const selectedOptionValuesArray = Object.entries(selectedOptionValues);
    return variants.filter((variant) =>
      selectedOptionValuesArray.every(([name, value]) =>
        variant.selectedOptions.some(
          (selectedOption) =>
            selectedOption.name === name && selectedOption.value === value,
        ),
      ),
    );
  }, [variants, selectedOptionValues]);

  useEffect(() => {
    const newVariants = Object.entries(selectedQuantities).map(
      ([optionValue, quantity]) => {
        const variant = matchingVariants.find(
          (variant) =>
            variant.selectedOptions[variant.selectedOptions.length - 1]
              .value === optionValue,
        );

        return {
          variant,
          quantity,
          monogram: selectedMonograms[optionValue] || null,
        };
      },
    );

    setSelectedVariants(newVariants);
  }, [selectedQuantities, selectedMonograms, matchingVariants]);

  // A single variant stands in for the whole selection — shown by layouts with
  // a single image, and morphed to by the page. The initial variant wins while
  // it matches, so the page isn't morphed away from the variant it rendered.
  const previewVariant = useMemo(
    () =>
      matchingVariants.find(({ id }) => id === initialVariantId) ||
      matchingVariants.find(({ image }) => image) ||
      matchingVariants[0] ||
      null,
    [matchingVariants, initialVariantId],
  );

  useEffect(() => {
    if (!setPreviewVariant) return;

    setPreviewVariant(previewVariant);
  }, [previewVariant, setPreviewVariant]);

  useEffect(() => {
    if (!previewVariant) return;

    ref.current.dispatchEvent(
      new CustomEvent("variant:changed", {
        bubbles: true,
        detail: { variant: previewVariant },
      }),
    );
  }, [previewVariant]);

  const updateOptionValue = (newValue, optionName) => {
    if (!optionValueExists({ options, optionName, optionValue: newValue }))
      return;

    const newSelectedOptionValues = {
      ...selectedOptionValues,
    };

    newSelectedOptionValues[optionName] = newValue;
    setSelectedOptionValues(newSelectedOptionValues);
  };

  useEffect(() => {
    const unifiedVariants = unifyGroupVariants(productData);

    setVariants(unifiedVariants);

    const unifiedOptions = unifyGroupOptions(productData);
    const selectableOptions = unifiedOptions.slice(0, -1);
    setOptions(selectableOptions);

    const resolvedValues = resolveInitialOptionValues({
      product: productData,
      initialVariantId,
    });
    const initialValues = Object.fromEntries(
      selectableOptions.map(({ optionName }) => [
        optionName,
        resolvedValues[optionName],
      ]),
    );

    setSelectedOptionValues(initialValues);
  }, [productData, initialVariantId, setSelectedOptionValues]);

  useEffect(() => {
    const unifiedOptions = unifyGroupOptions(productData);
    if (unifiedOptions.length) {
      setSelectedQuantities({
        [unifiedOptions[unifiedOptions.length - 1].optionValues[0].name]: 1,
      });
    }
  }, [productData, addedToCart, setSelectedQuantities]);

  return (
    <div
      className={`product-options ${variants.length ? "product-options--loaded" : ""}`}
      ref={ref}
    >
      <IdPrefixContext.Provider value={idPrefix}>
        {options.map((option, i) => (
          <ProductOption
            key={option.optionName}
            option={option}
            updateOptionValue={(newValue) =>
              updateOptionValue(newValue, option.optionName)
            }
            selectedOptionValue={selectedOptionValues[option.optionName]}
            allowMaterialGroups={allowMaterialGroups}
            truncate={truncate}
            swatchRequestUrl={swatchRequestUrl}
          />
        ))}
        {productData?.sizeGuide && (
          <SizeGuideTrigger sizeGuide={productData.sizeGuide} />
        )}
        <div className="product-options__variants">
          {matchingVariants.map((variant) => (
            <ProductOptionVariant
              variant={variant}
              variantQuantity={selectedQuantityForVariant(variant)}
              setVariantQuantity={(quantity) =>
                setSelectedQuantityForVariant(variant, quantity)
              }
              monogram={monogramForVariant(variant)}
              setMonogram={(monogram) =>
                setMonogramForVariant(variant, monogram)
              }
              smallMonogram={smallMonogram}
              key={variant.id}
            />
          ))}
        </div>
      </IdPrefixContext.Provider>
      {variants.length === 0 && (
        <div className="product-options__skeleton"></div>
      )}
    </div>
  );
}
